import { useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { de } from 'date-fns/locale';
import Popup from "components/Popup/popup";
import commonStyles from "components/commonStyle/commonStyle.module.css";
import styles from "./Popup.module.css";
import "react-datepicker/dist/react-datepicker.css";
import { BookingFormValues, BookingPopupProps } from "modules/booking-popup";
import { WorkTimeSlot, HolidaySlot } from "modules/calendar-worktime-holiday";
import { fetchHolidaySlots, fetchWorkSlots } from "services/workTime/calendar-service";
import Select from 'react-select'
import { GoInfo } from "react-icons/go";
import { getallKids } from "services/get-all-kids";
import { addDays, addWeeks, customSelectStyles, formatDate, getSlotsInDate } from "utils/bookingPopup";
import ErrorFailed from "components/errorFailed/errorFailed";
import { KidsData } from "modules/profile";
import { setsEmployBooking } from "services/booking/set-employ-booking";
import { FaArrowRight } from "react-icons/fa";
import SuccessPopup from "components/successPopup/successPopup";

registerLocale('de', de);

export default function EmployBookingPopup({ isOpen, onClose, onChange }: BookingPopupProps) {
  const [formValues, setFormValues] = useState<BookingFormValues>({
    kid: "",
    block: [],
    recurrence: "",
    category: "",
    note: "",
    recurrenceDates: [null, null, null, null],
    recurrenceStatuses: [false, false, false, false],

  });

  const [workTimeSlots, setWorkTimeSlots] = useState<WorkTimeSlot[]>([]);
  const [kidsData, setKidsData] = useState<KidsData[]>([]);
  const [holidaySlots, setHolidaySlots] = useState<HolidaySlot[]>([]);
  const [filteredBlocks, setFilteredBlocks] = useState<WorkTimeSlot[]>([]);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const selectedKid = kidsData ? kidsData.find(kid => kid.id.toString() == formValues.kid) : undefined;
  const [error, setError] = useState<string | null>(null);
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleInfoOpen = () => setIsInfoOpen(true);
  const handleInfoClose = () => setIsInfoOpen(false);

  const getSlotsData = async () => {
    try {
      const workData = await fetchWorkSlots();
      const holidayData = await fetchHolidaySlots();
      const kidsData = await getallKids();
      setKidsData(kidsData.kids);
      setWorkTimeSlots(workData);
      setHolidaySlots(holidayData);
    } catch (error) {
      console.error("Fehler beim Abrufen der daten:", error);
    }
  };


  useEffect(() => {
    getSlotsData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => {
      const updatedValues = {
        ...prevValues,
        [name]: value,
        'age': selectedKid ? selectedKid.age : 0
      };

      if (name === 'recurrence' && value === 'serial' && prevValues.bookingDate) {

        const recurrenceDates: any[] = [];
        for (let i = 0; i < 4; i++) {
          const nextDate = addWeeks(new Date(prevValues.bookingDate), i);
          recurrenceDates.push(nextDate);
        }
        updatedValues.recurrenceDates = recurrenceDates;
        updatedValues.recurrenceStatuses = [true, true, true, true];
      } else if (name === 'recurrence' && value !== 'serial') {
        updatedValues.recurrenceDates = [null, null, null, null];
        updatedValues.recurrenceStatuses = [false, false, false, false];
      }

      return updatedValues;
    });
  };


  const kidOptions = kidsData ? kidsData.map((kid) => ({
    value: kid.id.toString(),
    label: `${kid.first_name} ${kid.last_name}`,
  })) : undefined;

  const handleDateChange = (date: Date | null) => {
    formValues.block = [];

    if (date) {
      setFilteredBlocks([]);
      setFormValues((prevValues) => {
        const updatedValues = { ...prevValues, bookingDate: date, block: [] };
        if (updatedValues.recurrence === 'serial') {
          const recurrenceDates: any[] = [];
          for (let i = 0; i < 4; i++) {
            const nextDate = addWeeks(new Date(date), i);
            recurrenceDates.push(nextDate);
          }
          updatedValues.recurrenceDates = recurrenceDates;
        }

        return updatedValues;
      });

      setFilteredBlocks(getSlotsInDate(date, workTimeSlots, holidaySlots));
    }
  };

  const handleRecurrenceChange = (index: number) => {
    setFormValues((prevValues) => {
      const newRecurrenceStatuses = [...prevValues.recurrenceStatuses];

      newRecurrenceStatuses[index] = !newRecurrenceStatuses[index];

      return {
        ...prevValues,
        recurrenceStatuses: newRecurrenceStatuses,
      };
    });
  };

  const handleClose = () => {
    setFormValues({
      kid: "",
      block: [],
      recurrence: "",
      category: "",
      note: "",
      recurrenceDates: [null, null, null, null],
      recurrenceStatuses: [false, false, false, false],
    });
    setFilteredBlocks([])
    setError(null)
    onClose();
  };
  const handleChangePopup = () => {
    handleClose();
    if (onChange) { onChange(); }
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);


    if (formValues.recurrence === 'serial' && !formValues.recurrenceStatuses.some((status: any) => status)) {
      setError('Bitte wählen Sie mindestens ein Wiederholungsdatum aus.');
      setIsLoading(false);
      return;
    }
    
    try {

      const response = await setsEmployBooking({
        ...formValues,
        block: formValues.block,
        recurrence_date_0_status: formValues.recurrenceStatuses[0] ? 'confirmed' : '',
        recurrence_date_1_status: formValues.recurrenceStatuses[1] ? 'confirmed' : '',
        recurrence_date_2_status: formValues.recurrenceStatuses[2] ? 'confirmed' : '',
        recurrence_date_3_status: formValues.recurrenceStatuses[3] ? 'confirmed' : '',
      });
      if (!response.success) {
        setError(response.message || 'Es ist ein Fehler bei der Buchung aufgetreten. Bitte versuchen Sie es erneut.');
        setIsLoading(false);
      } else {
        setIsSuccessPopupOpen(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error booking request:", error);
      setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
      setIsLoading(false);
    }
  };


  if (!isOpen) return null;
  return (
    <>
      <Popup
        title={"Neue Buchungsanfrage"}
        submitLabel={""}
        closeLabel={""}
        isWithButton={false}
        isOpen={true}
        onClose={handleClose}
      >
        <form onSubmit={handleSubmit}>
          {error && <ErrorFailed error={error} />}
          <div className={styles.form}>
            <div className={commonStyles.formGroup}>
              <label htmlFor="bookingDate">Datum*</label>
              <DatePicker
                selected={formValues.bookingDate}
                onChange={handleDateChange}
                dateFormat="EEEE, dd. MMMM yyyy"
                placeholderText="Wählen Sie ein Datum"
                wrapperClassName={commonStyles.datepickerWrapperOverride}
                id="bookingDate"
                name="bookingDate"
                locale="de"
              />
            </div>
            <div className={commonStyles.formGroupRow}>
              <div className={commonStyles.formGroup}>
                <label htmlFor="kid">Kind*</label>
                <Select
                  id="kid"
                  name="kid"
                  options={kidOptions}
                  onChange={(selectedOption) => {
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      kid: selectedOption ? selectedOption.value : '',
                    }));
                  }}
                  placeholder="auswählen ..."
                  styles={customSelectStyles}
                  required

                />
                <div className={commonStyles.goToOtherbooking} onClick={handleChangePopup}>
                  <p> unangemeldetes Kind  </p> <FaArrowRight />

                </div>
              </div>
              <div className={commonStyles.formGroupOld}>
                <p>{selectedKid ? `${selectedKid.age} Jahre` : "0 Jahre"}</p>
              </div>
            </div>
            <div className={commonStyles.formGroupRow}>
              <div className={commonStyles.formGroup}>
                <label htmlFor="block">Blöcke*</label>
                <Select
                required
                  isMulti
                  options={filteredBlocks.map((slot) => ({
                    value: slot.id!.toString(),
                    label: `${slot.start_time.substring(0, 5)} - ${slot.end_time.substring(0, 5)}${slot.number_of_places === 0 ? ' (ausgebucht)' : ''}`,
                    isDisabled: slot.number_of_places === 0,
                  }))}
                  value={formValues.block.map((blockId) => ({
                    value: blockId,
                    label: filteredBlocks.find((slot) => slot.id?.toString() === blockId)
                      ? `${filteredBlocks.find((slot) => slot.id?.toString() === blockId)?.start_time.substring(0, 5)} - ${filteredBlocks.find((slot) => slot.id?.toString() === blockId)?.end_time.substring(0, 5)}`
                      : '',
                  }))}
                  onChange={(selectedOptions) => {
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      block: selectedOptions ? selectedOptions.map((option) => option.value) : [],
                    }));
                  }}
                  placeholder="Wählen Sie Blöcke aus ..."
                  styles={customSelectStyles}
                />
              </div>

              <div className={commonStyles.formGroup}>
                <label htmlFor="recurrence">
                  Wiederholung* <GoInfo onClick={handleInfoOpen} />
                </label>
                <select
                  id="recurrence"
                  name="recurrence"
                  value={formValues.recurrence}
                  onChange={(e) => {
                    handleInputChange(e);

                  }}
                  required
                >

                  <option value="" disabled>
                    auswählen ...
                  </option>
                  <option value="onetime">Einmalig</option>
                  <option value="serial">Wöchentlich</option>
                </select>
              </div>
            </div>
            {formValues.recurrence === "serial" && (
              <div className={commonStyles.formGroup}>
                <label>Enddatum: {formatDate(formValues.recurrenceDates[3])}</label>


                <div className={styles.chaeckBoxCountainer}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className={styles.recurrenceCheckbox}>
                      <input
                        type="checkbox"
                        checked={!!formValues.recurrenceStatuses[index]}
                        onChange={() => handleRecurrenceChange(index)}
                      />
                      <span>
                        {addDays(new Date(formValues.bookingDate ? formValues.bookingDate : ''), (index) * 7).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className={commonStyles.formGroup}>
              <label htmlFor="category">Kategorie*</label>
              <select
                id="category"
                name="category"
                value={formValues.category}
                onChange={handleInputChange}
                required
              >
                <option value="" disabled>
                  auswählen ...
                </option>
                <option value="care">Betreuung</option>
                <option value="integration">Eingewöhnung</option>
              </select>
            </div>
            <div className={commonStyles.formGroup}>
              <h3>
                <label htmlFor="note">Bemerkung</label>
              </h3>
              <textarea
                id="note"
                name="note"
                value={formValues.note}
                onChange={handleInputChange}
                className={styles.textarea}
              />
            </div>
            <div className={commonStyles.formGroup}>
              <p>
                Nach dem Absenden überprüfen wir deine Anfrage und du erhältst eine Bestätigungsemail an die angegebene E-Mailadresse.
              </p>
            </div>
          </div>

          <div className={styles.popupButtonContainer}>
            <button type="button" className={`${commonStyles.commonButton} ${commonStyles.backButton}`} onClick={handleClose}>
              Abbrechen
            </button>
            <button disabled={isLoading} type="submit" className={`${commonStyles.commonButton} ${commonStyles.submitButton}`} >
              {isLoading ? (
                <div className={commonStyles.spinner}></div>
              ) : (
                "Buchung bestätigen"
              )}

            </button>
          </div>

        </form>
      </Popup >

      {isInfoOpen && (
        <Popup
          title={"Information zur Wiederholung"}
          submitLabel={""}
          closeLabel={""}
          isWithButton={false}
          isOpen={true}
          onClose={handleInfoClose}
        >
          <p>
            Wöchentliche Termine gelten für ausgewählte Woche und die nächsten drei Wochen. Schließtage sowie bereits ausgebuchte Tage werden automatisch aus der Anfrage entfernt.
          </p>

        </Popup>
      )
      }
      <SuccessPopup isPopupOpen={isSuccessPopupOpen} onClose={() => {
        handleClose();
        setIsSuccessPopupOpen(false);
      }} />
    </>
  );
}
